import { useMutation } from 'react-query';

import { calculateTotalPrice } from '~/api/pricing';
import { useReduxState } from '~/hooks';
import { Draft } from '~/types/Draft';
import { PricingDto } from '~/types/PricingDto';
import { Services } from '~/types/Services';

import { ServicesTotalPriceRequestBody } from '~/types/ServicesTotalPriceType';
import { useGetServices } from './pricing';

const totalPriceKey = ['services', 'totalPrice'];

export const useCalculateTotalPrice = () => {
  const [, setState] = useTotalPriceState();
  return useMutation(totalPriceKey, calculateTotalPrice, {
    onMutate() {
      setState((prev) => ({ ...prev, isLoading: true, isError: false }));
    },
    onSuccess(res) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        data: res,
        isError: false,
        requiresRecalculate: false,
      }));
    },
    onError() {
      setState((prev) => ({ ...prev, isLoading: false, data: undefined, isError: true }));
    },
  });
};

export const useTotalPriceState = () => {
  return useReduxState<{
    data: PricingDto | undefined;
    isLoading: boolean;
    isError: boolean;
    requiresRecalculate: boolean;
  }>('totalPrice', {
    data: undefined,
    isLoading: false,
    isError: false,
    requiresRecalculate: false,
  });
};

export const useTotalPriceController = () => {
  const calculateTotalPriceMutation = useCalculateTotalPrice();
  const servicesQuery = useGetServices();

  const mutate = (formValues: Draft<'services'>) => {
    return servicesQuery.data && isProductTerminalFeeValid(formValues, servicesQuery.data.services)
      ? calculateTotalPriceMutation.mutate(getSelectedProducts(formValues, servicesQuery.data.services))
      : null;
  };

  const mutateAsync = (formValues: Draft<'services'>) => {
    return servicesQuery.data && isProductTerminalFeeValid(formValues, servicesQuery.data.services)
      ? calculateTotalPriceMutation.mutateAsync(getSelectedProducts(formValues, servicesQuery.data.services))
      : null;
  };

  return { mutate, mutateAsync };
};

const getSelectedProducts = (
  data: Draft<'services'> | undefined,
  services: Services['services'],
): ServicesTotalPriceRequestBody => {
  const result: ServicesTotalPriceRequestBody = {
    selectedProducts: {},
  };
  if (data?.services?.nGeniusPos?.enabled && services.find((item) => item.key === 'nGeniusPos')) {
    result.selectedProducts.nGeniusPos = [];
    const numberOfDevices = data.services.nGeniusPos.terminalFee;
    if (numberOfDevices) {
      const params = getRequiresNumberParams('nGeniusPos', services, numberOfDevices);
      result.selectedProducts.nGeniusPos.push(...params);
    }
  }
  if (data?.services?.eCommerce?.enabled && services.find((item) => item.key === 'eCommerce')) {
    result.selectedProducts.eCommerce = [];
    const numberOfDevices = 1;
    const params = getRequiresNumberParams('eCommerce', services, numberOfDevices);
    result.selectedProducts.eCommerce.push(...params);
  }
  if (data?.services?.nGeniusGo?.enabled && services.find((item) => item.key === 'nGeniusGo')) {
    result.selectedProducts.nGeniusGo = [];
    const numberOfDevices = data.services.nGeniusGo.terminalFee;
    if (numberOfDevices) {
      const params = getRequiresNumberParams('nGeniusGo', services, numberOfDevices);
      result.selectedProducts.nGeniusGo.push(...params);
    }
  }

  if (data?.services?.CORPORATE_EXECUTIVE_CARD?.enabled && services.find((item) => item.key === 'CORPORATE_EXECUTIVE_CARD')) {
    result.selectedProducts.CORPORATE_EXECUTIVE_CARD = [];
    const numberOfDevices = data.services.CORPORATE_EXECUTIVE_CARD.terminalFee;
    if (numberOfDevices) {
      const params = getRequiresNumberParams('CORPORATE_EXECUTIVE_CARD', services, numberOfDevices);
      result.selectedProducts.CORPORATE_EXECUTIVE_CARD.push(...params);
    }
  }

  if (data?.services?.DIGITAL_BUSINESS_PAYMENT?.enabled && services.find((item) => item.key === 'DIGITAL_BUSINESS_PAYMENT')) {
    result.selectedProducts.DIGITAL_BUSINESS_PAYMENT = [];
    const numberOfDevices = data.services.DIGITAL_BUSINESS_PAYMENT.terminalFee;
    if (numberOfDevices) {
      const params = getRequiresNumberParams('DIGITAL_BUSINESS_PAYMENT', services, numberOfDevices);
      result.selectedProducts.DIGITAL_BUSINESS_PAYMENT.push(...params);
    }
  }

  if (data?.services?.DIGITAL_TRAVEL_PAYMENT?.enabled && services.find((item) => item.key === 'DIGITAL_TRAVEL_PAYMENT')) {
    result.selectedProducts.DIGITAL_TRAVEL_PAYMENT = [];
    const numberOfDevices = data.services.DIGITAL_TRAVEL_PAYMENT.terminalFee;
    if (numberOfDevices) {
      const params = getRequiresNumberParams('DIGITAL_TRAVEL_PAYMENT', services, numberOfDevices);
      result.selectedProducts.DIGITAL_TRAVEL_PAYMENT.push(...params);
    }
  }

  return result;
};

const getRequiresNumberParams = (serviceName: string, services: Services['services'], deviceNumber: number) => {
  const fieldNamesWithRequiresNumber = services
    .find((item) => item.key === serviceName)
    ?.fields?.filter((field) => ('requiresNumber' in field ? field.requiresNumber : false))
    ?.map((field) => field.key);

  if (!fieldNamesWithRequiresNumber) return [];

  return fieldNamesWithRequiresNumber?.map((fieldName) => ({
    key: fieldName,
    input: deviceNumber,
  }));
};

const isProductTerminalFeeValid = (data: Draft<'services'> | undefined, services: Services['services']): boolean => {

  const validationResult = false;

  // if (data?.services) {
  //   Object.entries(data.services).forEach((entry) => {
  //     const requiresNumber = services
  //       .find((item) => item.key === entry[0])
  //       ?.fields.filter((item) => item.key === 'terminalFee')
  //       ?.find((field) => ('requiresNumber' in field ? field.requiresNumber : false));

  //     if (entry[1].enabled && requiresNumber && !entry[1].terminalFee) {
  //       validationResult = false;
  //     }
  //   });
  // }

  return validationResult;
};
